import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChatService } from '../../services/chat.service';
import Swal from 'sweetalert2';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

interface Message { text: string; rawHtml?: string; html?: SafeHtml; sender: 'user'|'bot'; timestamp?: Date; suggestedPrompts?: string[] }

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.css']
})
export class ChatComponent implements OnInit {
  @ViewChild('messageContainer') messageContainer!: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef;
  messages: Message[] = [];
  userInput = '';
  sessionId = '';
  isTyping = false;
  isFocused = false;
  escalationActive: boolean = false;
  escalationMessage: string = '';
  // Off-canvas menu state for small screens
  sideMenuOpen: boolean = false;
  storageKey = 'chat_history_';
  isExited: boolean = false;

  constructor(private chatService: ChatService, private sanitizer: DomSanitizer) {}

  ngOnInit() {
    // Restore or create session id so conversation persists across refresh
    const existingSid = localStorage.getItem('chat_session_id');
    if (existingSid) {
      this.sessionId = existingSid;
    } else {
      this.sessionId = 'session_' + Date.now();
      localStorage.setItem('chat_session_id', this.sessionId);
    }

    // Load persisted messages for this session if any
    const raw = localStorage.getItem(this.storageKey + this.sessionId);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        this.messages = parsed.map((m: any) => ({
          text: m.text,
          rawHtml: m.rawHtml || m.text,
          html: this.sanitizer.bypassSecurityTrustHtml(m.rawHtml || m.text),
          sender: m.sender,
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
        }));
      } catch(e) { this.messages = []; }
    }

    if (!this.messages || this.messages.length === 0) {
      this.addBotMessage("Hello! 👋 I'm JoyBot, your compassionate AI companion for mental health support. I'm here to listen, understand, and help you navigate feelings of anxiety and depression. You're not alone in this journey. How are you feeling today?");
      this.saveHistory();
    }
    this.scrollToBottom(); // Scroll to show welcome or restored messages
  }

  sendMessage() {
    const msg = this.userInput.trim();
    if (!msg) return;
  this.messages.push({ text: msg, rawHtml: this.escapeHtml(msg).replace(/\n/g,'<br/>'), html: this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(msg).replace(/\n/g,'<br/>')), sender: 'user', timestamp: new Date() });
  this.userInput = '';
  this.scrollToBottom(); // Scroll immediately after user message
  // Shrink textarea immediately after sending so UI returns to compact state
  try { this.collapseInput(); } catch(e) {}
  this.isTyping = true;
  // Ensure the UI scrolls to show the typing/loader bubble that appears when isTyping=true
  setTimeout(() => { try { this.scrollToBottom(); } catch(e) {} }, 50);
    // detect a starter hint based on simple heuristics
    const starter = this.detectStarter(msg);
    this.saveHistory();
    this.chatService.sendMessage(this.sessionId, msg, this.messages, starter).subscribe({ next: (res) => {
      this.isTyping = false;
      if (res && res.success) {
        const raw = res.response || '';
        // Decode any HTML entities returned by backend, then format into safe HTML
        const decoded = this.decodeHtmlEntities(raw);
        const formatted = this.formatBotResponse(decoded);
        const suggested = this.generateSuggestedPrompts(msg, decoded);
        this.messages.push({ text: this.stripTags(formatted), rawHtml: formatted, html: this.sanitizer.bypassSecurityTrustHtml(formatted), sender: 'bot', timestamp: new Date(), suggestedPrompts: suggested });
        // Show escalation banner when backend flags escalation
        try {
          this.escalationActive = !!res.escalated;
          if (this.escalationActive) {
            this.escalationMessage = 'Connecting you to an available representative — please stay on this chat.';
          } else {
            this.escalationMessage = '';
          }
        } catch(e) { this.escalationActive = false; this.escalationMessage = ''; }
      } else {
        const fallback = 'Sorry, an error occurred.';
        this.messages.push({ text: fallback, rawHtml: fallback, html: this.sanitizer.bypassSecurityTrustHtml(fallback), sender: 'bot', timestamp: new Date() });
      }
      this.saveHistory();
      this.scrollToBottom();
      // collapse input after send
      this.collapseInput();
    }, error: (err) => {
      this.isTyping = false;
      console.error('Chat API Error:', err);
      this.messages.push({ text: 'Sorry, unable to reach server.', rawHtml: 'Sorry, unable to reach server.', html: this.sanitizer.bypassSecurityTrustHtml('Sorry, unable to reach server.'), sender: 'bot', timestamp: new Date() });
      // clear escalation state on error
      this.escalationActive = false;
      this.escalationMessage = '';
      this.scrollToBottom();
      this.collapseInput();
    }});
  }

  refreshChat() {
    // ensure side menu is closed when refreshing
    try { this.closeMenu(); } catch(e) {}
    // Clear messages
    this.messages = [];
    
    // Generate new session ID
    this.sessionId = 'session_' + Date.now();
    localStorage.setItem('chat_session_id', this.sessionId);
    
    // Clear stored history
    localStorage.removeItem(this.storageKey + this.sessionId);
    
    // Reset escalation state
    this.escalationActive = false;
    this.escalationMessage = '';
    
    // Reset input
    this.userInput = '';
    
    // Add welcome message
    this.addBotMessage("Hello! 👋 I'm JoyBot, your compassionate AI companion for mental health support. I'm here to listen, understand, and help you navigate feelings of anxiety and depression. You're not alone in this journey. How are you feeling today?");
    
    // Save the new state
    this.saveHistory();
    
    // Scroll to show welcome message
    this.scrollToBottom();
  }

  toggleMenu() {
    this.sideMenuOpen = !this.sideMenuOpen;
  }

  closeMenu() {
    this.sideMenuOpen = false;
  }

  confirmExit(): void {
    Swal.fire({
      title: 'Leave JoyBot?',
      text: 'Would you like to close your session? Your current conversation will be cleared for your privacy.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, Exit',
      cancelButtonText: 'Stay Here'
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Clear session history for privacy
        localStorage.removeItem('chat_session_id');
        localStorage.removeItem(this.storageKey + this.sessionId);

        // If running in Android/iOS native app, exit application
        if (Capacitor.isNativePlatform()) {
          try {
            await App.exitApp();
            return;
          } catch (e) {
            console.warn('Native exitApp error:', e);
          }
        }

        // On Web: lead out of the chatbot to the dedicated Session Safely Closed view
        this.isExited = true;
        this.messages = [];
        this.userInput = '';
        this.escalationActive = false;
      }
    });
  }

  reopenChat(): void {
    this.isExited = false;
    this.refreshChat();
  }

  dismissEscalation() {
    this.escalationActive = false;
    this.escalationMessage = '';
  }

  private saveHistory() {
    try {
      const toSave = this.messages.map(m => ({ text: m.text, rawHtml: m.rawHtml || m.text, sender: m.sender, timestamp: m.timestamp ? m.timestamp.toString() : new Date().toString() }));
      localStorage.setItem(this.storageKey + this.sessionId, JSON.stringify(toSave));
    } catch(e) {}
  }

  private detectStarter(text: string): string {
    const t = text.toLowerCase();
    // Mental health context detection
    if (/thanks|thank you|ty|better|helped|grateful/.test(t)) return 'I\'m glad to hear that.';
    if (/anxious|worried|scared|afraid|nervous/.test(t)) return 'I understand.';
    if (/depressed|sad|hopeless|empty|alone/.test(t)) return 'I hear you.';
    if (/suicide|kill myself|end it|die/.test(t)) return 'I\'m concerned about you.';
    if (t.endsWith('?') || /how|why|what|when|where|who/.test(t)) return 'That\'s a good question.';
    if (/stressed|overwhelmed|pressure/.test(t)) return 'That sounds really difficult.';
    return 'I\'m listening.';
  }

  onEnter(event: KeyboardEvent) {
    // If user pressed Enter without Shift => send message
    if (event && (event as KeyboardEvent).shiftKey) {
      return; // allow newline
    }
    event.preventDefault();
    this.sendMessage();
  }

  onFocus() {
    this.isFocused = true;
    // expand textarea visually
    try { this.messageInput.nativeElement.classList.add('input-expanded'); } catch(e) {}
  }

  onBlur() {
    // keep expanded until message is sent; but if textarea empty, collapse
    if (!this.userInput) {
      this.collapseInput();
    }
  }

  collapseInput() {
    this.isFocused = false;
    try { this.messageInput.nativeElement.classList.remove('input-expanded'); this.messageInput.nativeElement.blur(); } catch(e) {}
  }

  private addBotMessage(text: string) {
    // Format bot message text into HTML (preserve apostrophes and simple lists/paragraphs)
    const formatted = this.formatBotResponse(text);
    const suggested = this.generateSuggestedPrompts('', text);
    this.messages.push({ text: this.stripTags(formatted), rawHtml: formatted, html: this.sanitizer.bypassSecurityTrustHtml(formatted), sender: 'bot', timestamp: new Date(), suggestedPrompts: suggested });
  }

  // Generate two concise suggested prompts that start with a question word
  // (what/why/which/list/how/etc.) and are contextual to the user's question.
  private generateSuggestedPrompts(userMsg: string, botText: string): string[] {
    const raw = (userMsg && userMsg.length > 0) ? userMsg : botText || '';
    const seed = raw.replace(/\s+/g, ' ').trim().replace(/\?+$/,'');
    if (!seed) return ['How to get started?', 'Examples and use-cases.'];

    // Extract a concise topic by removing common question prefixes and trailing clauses
    let topic = seed.toLowerCase();
    // Patterns like "what is X", "what are X", "how to X", "can you help me X"
    topic = topic.replace(/^what(?:'s| is| are)\s+/i, '');
    topic = topic.replace(/^how(?: do i| to| can i)\s+/i, '');
    topic = topic.replace(/^can you(?: help(?: me)?)?\s+(?:with\s+)?/i, '');
    topic = topic.replace(/^please\s+/i, '');
    // remove trailing phrases after conjunctions to keep topic short
    topic = topic.split(/\band\b|\bor\b|\babout\b|\bfor\b|\bin\b/)[0].trim();
    // remove leading articles
    topic = topic.replace(/^the\s+|^a\s+|^an\s+/i, '').trim();
    if (!topic) topic = seed.split(/[\n\.]/)[0].trim();

    // Capitalize for display
    const topicDisplay = topic.split(' ').map(w => w[0] ? w[0].toUpperCase()+w.slice(1) : '').join(' ');

    const templates = [
      `How to use ${topicDisplay}`,
      `What are the common use cases for ${topicDisplay}`,
      `Steps to get started with ${topicDisplay}`,
      `Examples of ${topicDisplay} in practice`,
      `How to customize ${topicDisplay} without coding`,
      `Checklist to set up ${topicDisplay}`
    ];

    const idx = Math.abs(this.hashCode(topic)) % templates.length;
    const p1 = templates[idx];
    const p2 = templates[(idx + 2) % templates.length];
    return [p1, p2];
  }

  // simple deterministic hash for selection
  private hashCode(str: string): number {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = (h << 5) - h + str.charCodeAt(i);
      h |= 0;
    }
    return h;
  }

  useSuggestedPrompt(prompt: string) {
    if (!prompt) return;
    // Simulate user typing and sending
    this.userInput = prompt;
    // Ensure input is visible and focused briefly
    try { this.messageInput.nativeElement.focus(); } catch(e) {}
    // send after a tick so bindings update
    setTimeout(() => this.sendMessage(), 80);
  }

  // Escape HTML to avoid injection when converting text -> HTML
  private escapeHtml(str: string): string {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\"/g, '&quot;').replace(/'/g, '&#039;');
  }

  // Strip tags for plain-text fallback display
  private stripTags(input: string): string {
    return input ? input.replace(/<[^>]*>?/gm, '') : '';
  }

  // Decode common HTML entities (backend may return encoded apostrophes etc.)
  private decodeHtmlEntities(str: string): string {
    if (!str) return '';
    return str.replace(/&#039;|&apos;/g, "'")
              .replace(/&quot;/g, '"')
              .replace(/&amp;/g, '&')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>');
  }

  // Convert simple plaintext into basic HTML (paragraphs, unordered and ordered lists).
  // If the input already contains HTML tags, return as-is.
  private formatBotResponse(input: string): string {
    if (!input) return '';
    let decoded = this.decodeHtmlEntities(input);
    // Strip any trailing "Sources:"/"Source:" sections (plain text or simple HTML line-break variants)
    decoded = decoded.replace(/(?:<br\s*\/?>(?:\s|\u00A0)*)*\s*Sources?:[\s\S]*$/i, '');
    decoded = decoded.replace(/\n\s*Sources?:[\s\S]*$/i, '');
    // We'll convert leading '*' or '-' markers into proper unordered lists below.
    // If backend produced HTML, leave it alone
    if (/<\/?[a-z][\s\S]*>/i.test(decoded)) return decoded;

    const lines = decoded.split(/\r?\n/).map(l => l.trim());
    let out = '';
    let inUl = false;
    let inOl = false;

    for (let line of lines) {
      if (!line) {
        // empty line closes any open lists and adds spacing
        if (inUl) { out += '</ul>'; inUl = false; }
        if (inOl) { out += '</ol>'; inOl = false; }
        continue;
      }

      const ulMatch = /^[\-\*]\s+(.+)$/.exec(line);
      const olMatch = /^\d+\.\s+(.+)$/.exec(line);

      if (ulMatch) {
        if (!inUl) { if (inOl) { out += '</ol>'; inOl = false; } out += '<ul>'; inUl = true; }
        out += '<li>' + this.escapeHtml(ulMatch[1]) + '</li>';
        continue;
      }

      if (olMatch) {
        if (!inOl) { if (inUl) { out += '</ul>'; inUl = false; } out += '<ol>'; inOl = true; }
        out += '<li>' + this.escapeHtml(olMatch[1]) + '</li>';
        continue;
      }

      // plain paragraph
      if (inUl) { out += '</ul>'; inUl = false; }
      if (inOl) { out += '</ol>'; inOl = false; }
      out += '<p>' + this.escapeHtml(line) + '</p>';
    }

    if (inUl) out += '</ul>';
    if (inOl) out += '</ol>';
    return out || ('<p>' + decoded + '</p>');
  }

  private scrollToBottom() {
    setTimeout(() => {
      try {
        const container = this.messageContainer.nativeElement;
        // Scroll to the true bottom so newest messages are visible
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      } catch(e) {}
    }, 50);
  }
}
